import React, { useState } from "react";
import classNames from "classnames";
import { XMarkIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { FinanceStore, reconciledTxIds, expectsStatement } from "./finance-store";
import { MODULES, CHANNELS, AGENCES, PeriodKey, periodRange, inRange, money, fDateTime, downloadCsv, todayIso, addDaysIso, Transaction, FinStatus } from "./finance-data";
import { FinPage, Kpi, Card, PeriodPicker, STATUS_TONE, btnGhost, Toast } from "./finance-ui";

export default function FinanceTransactions({ store }: { store: FinanceStore }) {
  const { txs, setTxs } = store;
  const [period, setPeriod] = useState<PeriodKey>("30d");
  const [from, setFrom] = useState(addDaysIso(todayIso(), -29));
  const [to, setTo] = useState(todayIso());
  const [q, setQ] = useState("");
  const [fModule, setFModule] = useState("Tous");
  const [fChannel, setFChannel] = useState("Tous");
  const [fType, setFType] = useState("Tous");
  const [fStatus, setFStatus] = useState("Tous");
  const [fAgence, setFAgence] = useState("Toutes");
  const [fRec, setFRec] = useState("Tous");
  const [sort, setSort] = useState<{ k: "date" | "amount"; dir: 1 | -1 }>({ k: "date", dir: -1 });
  const [detail, setDetail] = useState<Transaction | null>(null);
  const [limit, setLimit] = useState(50);
  const [toast, setToast] = useState<string | null>(null);
  const flash = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2600);
  };

  const range = periodRange(period, from, to);
  const rec = reconciledTxIds(store);
  const recLabel = (t: Transaction) => (!expectsStatement(t) ? (t.channel === "Espèces" ? "Caisse" : "—") : rec.has(t.id) ? "Rapprochée" : "À rapprocher");

  const rows = txs
    .filter((t) => {
      if (!inRange(t.date, range)) return false;
      if (fModule !== "Tous" && t.module !== fModule) return false;
      if (fChannel !== "Tous" && t.channel !== fChannel) return false;
      if (fType !== "Tous" && t.type !== fType) return false;
      if (fStatus !== "Tous" && t.status !== fStatus) return false;
      if (fAgence !== "Toutes" && t.agence !== fAgence) return false;
      if (fRec !== "Tous" && recLabel(t) !== fRec) return false;
      if (q.trim()) {
        const hay = [t.ref, t.opRef, t.dossier, t.client, t.agent].join(" ").toLowerCase();
        if (!hay.includes(q.trim().toLowerCase())) return false;
      }
      return true;
    })
    .sort((a, b) => (sort.k === "date" ? a.date.localeCompare(b.date) : a.amount - b.amount) * sort.dir);

  const ok = rows.filter((t) => t.status === "Réussie");
  const inflow = ok.filter((t) => t.amount > 0).reduce((a, t) => a + t.amount, 0);
  const outflow = ok.filter((t) => t.amount < 0).reduce((a, t) => a + t.amount, 0);
  const fees = ok.reduce((a, t) => a + t.fee, 0);

  const setStatus = (t: Transaction, status: FinStatus, msg: string) => {
    setTxs((prev) => prev.map((x) => (x.id === t.id ? { ...x, status, fee: status === "Échouée" ? 0 : x.fee } : x)));
    setDetail((d) => (d && d.id === t.id ? { ...d, status } : d));
    flash(msg);
  };

  const sel = "h-9 px-2.5 border border-[#e7dccd] rounded-lg bg-white text-xs";
  const sortBtn = (k: "date" | "amount", label: string) => (
    <button type="button" onClick={() => setSort((s) => ({ k, dir: s.k === k ? (s.dir === 1 ? -1 : 1) : -1 }))} className="inline-flex items-center gap-1 uppercase">
      {label}
      <span className="text-[#92400e]">{sort.k === k ? (sort.dir === 1 ? "▲" : "▼") : ""}</span>
    </button>
  );

  return (
    <FinPage
      title="Transactions"
      subtitle={`${rows.length} opération${rows.length > 1 ? "s" : ""} sur la période · billets de bus, expéditions internationales et livraison urbaine`}
      actions={
        <button
          type="button"
          className={btnGhost}
          onClick={() =>
            downloadCsv(
              `transactions_${range[0]}_${range[1]}.csv`,
              ["Référence", "Date", "Activité", "Dossier", "Client", "Canal", "Réf. opérateur", "Type", "Montant", "Frais", "Statut", "Rapprochement", "Agence", "Agent"],
              rows.map((t) => [t.ref, t.date.replace("T", " "), t.module, t.dossier, t.client, t.channel, t.opRef, t.type, t.amount, t.fee, t.status, recLabel(t), t.agence, t.agent])
            )
          }
        >
          Exporter (CSV)
        </button>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="Encaissements" value={money(inflow)} sub={`${ok.filter((t) => t.amount > 0).length} opérations réussies`} tone="#047857" />
        <Kpi label="Sorties" value={money(outflow)} sub="remboursements et reversements CR" tone="#b91c1c" />
        <Kpi label="Frais opérateurs" value={money(fees)} sub="déduits par les opérateurs" />
        <Kpi label="Net" value={money(inflow + outflow - fees)} sub={`${rows.filter((t) => t.status === "En attente").length} en attente · ${rows.filter((t) => t.status === "Échouée").length} échouées`} tone="#92400e" />
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <PeriodPicker value={period} onChange={setPeriod} from={from} to={to} onFrom={setFrom} onTo={setTo} />
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Référence, réf. opérateur, dossier, client, agent…" className="w-full h-9 pl-9 pr-3 border border-[#e7dccd] rounded-lg bg-white text-xs" />
        </div>
        <select value={fModule} onChange={(e) => setFModule(e.target.value)} className={sel}>
          <option value="Tous">Toutes activités</option>
          {MODULES.map((m) => (
            <option key={m}>{m}</option>
          ))}
        </select>
        <select value={fChannel} onChange={(e) => setFChannel(e.target.value)} className={sel}>
          <option value="Tous">Tous canaux</option>
          {CHANNELS.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <select value={fType} onChange={(e) => setFType(e.target.value)} className={sel}>
          <option value="Tous">Tous types</option>
          {["Encaissement", "Remboursement", "Reversement CR"].map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={sel}>
          <option value="Tous">Tous statuts</option>
          {["Réussie", "En attente", "Échouée"].map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
        <select value={fAgence} onChange={(e) => setFAgence(e.target.value)} className={sel}>
          <option value="Toutes">Toutes agences</option>
          {AGENCES.map((a) => (
            <option key={a}>{a}</option>
          ))}
        </select>
        <select value={fRec} onChange={(e) => setFRec(e.target.value)} className={sel}>
          <option value="Tous">Rapprochement : tous</option>
          {["Rapprochée", "À rapprocher", "Caisse"].map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[1100px]">
            <thead>
              <tr className="text-left text-[10.5px] font-bold tracking-wider uppercase text-[#8a7b6b] bg-[#fbf6ef]">
                <th className="px-4 py-3">Référence</th>
                <th className="px-3 py-3">{sortBtn("date", "Date")}</th>
                <th className="px-3 py-3">Activité / dossier</th>
                <th className="px-3 py-3">Client</th>
                <th className="px-3 py-3">Canal</th>
                <th className="px-3 py-3">Type</th>
                <th className="px-3 py-3">Statut</th>
                <th className="px-3 py-3">Rapprochement</th>
                <th className="px-4 py-3 text-right">{sortBtn("amount", "Montant")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, limit).map((t) => {
                const r = recLabel(t);
                return (
                  <tr key={t.id} onClick={() => setDetail(t)} className="border-t border-[#f5efe6] hover:bg-[#fdf8f1] cursor-pointer">
                    <td className="px-4 py-2.5 font-mono font-semibold text-[#92400e] whitespace-nowrap">{t.ref}</td>
                    <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{fDateTime(t.date)}</td>
                    <td className="px-3 py-2.5">
                      <div>{t.module}</div>
                      <div className="font-mono text-[11px] text-gray-500">{t.dossier}</div>
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">{t.client}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <div>{t.channel}</div>
                      {t.opRef && <div className="font-mono text-[11px] text-gray-500">{t.opRef}</div>}
                    </td>
                    <td className="px-3 py-2.5 whitespace-nowrap">{t.type}</td>
                    <td className="px-3 py-2.5">
                      <span className="px-2 py-0.5 rounded font-bold text-[11px] whitespace-nowrap" style={{ background: STATUS_TONE[t.status].bg, color: STATUS_TONE[t.status].fg }}>
                        {t.status}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={classNames("text-[11px] font-semibold", r === "Rapprochée" ? "text-emerald-700" : r === "À rapprocher" ? "text-amber-700" : "text-gray-500")}>{r}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <div className="font-bold" style={{ color: t.amount < 0 ? "#b91c1c" : "#1f1b2e" }}>
                        {money(t.amount)}
                      </div>
                      {t.fee > 0 && <div className="text-[11px] text-gray-500">frais {money(t.fee)}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && <div className="p-8 text-center text-xs text-gray-500">Aucune transaction pour ces critères.</div>}
        {rows.length > limit && (
          <div className="p-3 text-center border-t border-[#f5efe6]">
            <button type="button" onClick={() => setLimit((l) => l + 50)} className="text-xs font-semibold text-[#92400e]">
              Afficher 50 de plus ({rows.length - limit} restantes)
            </button>
          </div>
        )}
      </Card>

      {detail && (
        <div className="fixed inset-0 z-[85] flex items-center justify-center p-4 bg-[rgba(31,27,46,0.5)]" onMouseDown={() => setDetail(null)}>
          <div onMouseDown={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white rounded-2xl shadow-2xl text-xs overflow-hidden">
            <div className="px-5 py-4 border-b border-[#f5efe6] flex items-start">
              <div className="flex-1">
                <div className="font-mono text-sm font-bold text-[#92400e]">{detail.ref}</div>
                <div className="text-2xl font-bold mt-1" style={{ color: detail.amount < 0 ? "#b91c1c" : "#1f1b2e" }}>
                  {money(detail.amount)}
                </div>
                <span className="inline-block mt-1 px-2 py-0.5 rounded font-bold text-[11px]" style={{ background: STATUS_TONE[detail.status].bg, color: STATUS_TONE[detail.status].fg }}>
                  {detail.status}
                </span>
              </div>
              <button type="button" onClick={() => setDetail(null)} className="p-1 rounded hover:bg-gray-100 text-gray-500">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              {(
                [
                  ["Date", fDateTime(detail.date)],
                  ["Type", detail.type],
                  ["Activité", detail.module],
                  ["Dossier", detail.dossier],
                  ["Client", detail.client],
                  ["Canal", detail.channel],
                  ["Réf. opérateur", detail.opRef || "—"],
                  ["Frais opérateur", money(detail.fee)],
                  ["Net", money(detail.amount - detail.fee)],
                  ["Rapprochement", recLabel(detail)],
                  ["Agence", detail.agence],
                  ["Agent", detail.agent],
                ] as const
              ).map(([k, v]) => (
                <div key={k}>
                  <div className="text-[11px] text-gray-500">{k}</div>
                  <div className="font-semibold mt-0.5">{v}</div>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 bg-[#fbf8f4] border-t border-[#f5efe6] flex justify-end gap-2 flex-wrap">
              {detail.status === "En attente" && (
                <>
                  <button type="button" onClick={() => setStatus(detail, "Échouée", "Transaction marquée échouée")} className={btnGhost}>
                    Marquer échouée
                  </button>
                  <button type="button" onClick={() => setStatus(detail, "Réussie", "Réception confirmée")} className="h-9 px-4 bg-[#16a34a] rounded-lg text-xs font-bold text-white">
                    Confirmer la réception
                  </button>
                </>
              )}
              {detail.status === "Échouée" && (
                <button type="button" onClick={() => flash(`Nouveau lien de paiement envoyé à ${detail.client}`)} className="h-9 px-4 bg-[#92400e] rounded-lg text-xs font-bold text-white">
                  Relancer le paiement
                </button>
              )}
              <button type="button" onClick={() => flash(`Reçu ${detail.ref} envoyé à l'imprimante`)} className={btnGhost}>
                Imprimer le reçu
              </button>
            </div>
          </div>
        </div>
      )}
      <Toast msg={toast} />
    </FinPage>
  );
}
